import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react'
import { CheckCircle2, AlertCircle, X } from 'lucide-react'
import { cn } from '@/shared/lib/utils'

export type InputState = 'default' | 'error' | 'success'

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /**
   * Состояние поля ввода
   */
  state?: InputState
  /**
   * Подпись поля
   */
  label?: string
  /**
   * Сообщение об ошибке
   */
  errorMessage?: string
  /**
   * Сообщение об успехе
   */
  successMessage?: string
  /**
   * Подсказка под полем
   */
  hint?: string
  /**
   * Иконка слева
   */
  leftIcon?: ReactNode
  /**
   * Иконка справа
   */
  rightIcon?: ReactNode
  /**
   * Кнопка очистки поля
   */
  clearable?: boolean
  /**
   * Обработчик очистки
   */
  onClear?: () => void
}

/**
 * UI компонент: Поле ввода
 * 
 * Базовый компонент поля ввода с поддержкой состояний,
 * валидации и мобильной оптимизации.
 * 
 * @example
 * ```tsx
 * <Input
 *   label="Email"
 *   type="email"
 *   placeholder="Введите email"
 *   state={errors.email ? 'error' : 'default'}
 *   errorMessage={errors.email}
 * />
 * 
 * <Input
 *   label="Телефон"
 *   type="tel"
 *   leftIcon={<PhoneIcon />}
 *   clearable
 *   onClear={() => setValue('')}
 * />
 * ```
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      state = 'default',
      label,
      errorMessage,
      successMessage,
      hint,
      leftIcon,
      rightIcon,
      clearable = false,
      onClear,
      className,
      value,
      onChange,
      ...props
    },
    ref
  ) => {
    const showClearButton = clearable && value && !props.disabled && !props.readOnly

    const inputStyles = cn(
      // Базовые стили
      'w-full px-4 rounded-xl border-2 transition-all duration-150',
      'bg-white dark:bg-dark-card text-ink dark:text-white',
      'placeholder:text-gray-400 dark:placeholder:text-gray-500',
      'focus:outline-none',
      'disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-dark-elevated',
      'read-only:bg-gray-50 dark:read-only:bg-dark-elevated read-only:cursor-default',
      // Минимальная высота для touch target
      'min-h-[48px]',
      // Минимальный размер шрифта для предотвращения zoom на iOS
      'text-base',
      // Padding с учетом иконок
      {
        'pl-11': leftIcon,
        'pr-11': rightIcon || showClearButton,
        'pl-4': !leftIcon,
        'pr-4': !rightIcon && !showClearButton,
      },
      // Состояния
      {
        'border-silk dark:border-dark-border focus:border-primary dark:focus:border-primary-light':
          state === 'default',
        'border-error focus:border-error':
          state === 'error',
        'border-success focus:border-success':
          state === 'success',
      },
      className
    )

    const getStateIcon = () => {
      if (state === 'error') {
        return <AlertCircle className="h-5 w-5 text-error" aria-hidden="true" />
      }
      if (state === 'success') {
        return <CheckCircle2 className="h-5 w-5 text-success" aria-hidden="true" />
      }
      return null
    }

    const stateIcon = getStateIcon()
    const showRightIcon = stateIcon || (rightIcon && !showClearButton)

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={props.id}
            className="block text-sm font-medium text-ink dark:text-white mb-2"
          >
            {label}
            {props.required && <span className="text-error ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          {leftIcon && (
            <div
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              aria-hidden="true"
            >
              {leftIcon}
            </div>
          )}

          <input
            ref={ref}
            className={inputStyles}
            value={value}
            onChange={onChange}
            aria-invalid={state === 'error'}
            aria-describedby={
              errorMessage || successMessage || hint
                ? `${props.id}-description`
                : undefined
            }
            {...props}
          />

          {showRightIcon && (
            <div
              className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
              aria-hidden="true"
            >
              {stateIcon || rightIcon}
            </div>
          )}

          {showClearButton && (
            <button
              type="button"
              onClick={onClear}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors touch-target"
              aria-label="Очистить поле"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        {(errorMessage || successMessage || hint) && (
          <div
            id={`${props.id}-description`}
            className={cn(
              'mt-2 text-sm',
              {
                'text-error': errorMessage,
                'text-success': successMessage,
                'text-gray-500': hint && !errorMessage && !successMessage,
              }
            )}
            role={errorMessage ? 'alert' : undefined}
          >
            {errorMessage || successMessage || hint}
          </div>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'
